<script setup>
import { computed, onMounted, reactive, ref, watch, getCurrentInstance, nextTick } from 'vue'
import { router } from '@inertiajs/vue3';
import { watchDebounced, useStorage, useDebounceFn } from '@vueuse/core';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';
import RocketSvg from '@/assets/rocket-icon.svg?raw';
import RocketFillSvg from '@/assets/rocket-icon-fill.svg?raw';
import draggable from 'vuedraggable';
import Checkbox from 'primevue/checkbox';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import FileUpload from 'primevue/fileupload';
import Tag from 'primevue/tag';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import ToggleSwitch from 'primevue/toggleswitch';
import SitesFilters from '@/views/linkarme/components/SitesFilters.vue';
import SitesTable from '@/views/linkarme/components/SitesTable.vue';
import SyncBanner from '@/components/SyncBanner.vue';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const confirm = useConfirm();

const rocketSvg = computed(() => {
    return RocketSvg || '';
});

const rocketFillSvg = computed(() => {
    return RocketFillSvg || '';
});

// Mock coinStore para evitar erro do Pinia
const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const props = defineProps({
    title: String,
    sites: Object,
    coins: Object,
    countries: Array,
    languages: Array,
    categories: Array,
    sellers: Array,
    types: Array,
    sitesByCountry: Array,
    importFailures: Object,
    importErrors: Object,
    importAdded: Array,
    importUpdated: Array,
    pendingCount: Number,
    filters: Object,
});

const links = computed(() => {
    const _links = props.sites.links
    _links.shift()
    _links.pop()
    return _links
})

const sitesWithHighestSale = computed(() => {
    const _sites = JSON.parse(JSON.stringify(props.sites.data))

    _sites.forEach(site => {
        // Backend provides highest_sale in cents — keep in cents for formatConvertedPrice/formatCents
        site.highest_sale = site.highest_sale || 0
        site.highest_sale_coin_ratio = site.highest_sale_coin ? coinStore.ratios[site.highest_sale_coin] : 1
    })

    return _sites
})

// Define valid column keys to filter out old/invalid columns
const validColumnKeys = [
    'sale', 'url', 'da', 'dr', 'traffic', 'link_type', 'branded_content', 'types',
    'country', 'language', 'category', 'example',
    'inserted_at', 'last_updated_at',
];

// Column label map - keys only, labels added reactively
const columnDefinitions = {
    sale: { key: 'sale', sortable: true, defaultVisible: true },
    url: { key: 'url', sortable: true, defaultVisible: true },
    da: { key: 'da', sortable: true, defaultVisible: true },
    dr: { key: 'dr', sortable: true, defaultVisible: true },
    traffic: { key: 'traffic', sortable: true, defaultVisible: true },
    link_type: { key: 'link_type', sortable: false, defaultVisible: true },
    branded_content: { key: 'branded_content', sortable: false, defaultVisible: true },
    types: { key: 'types', sortable: false, defaultVisible: true },
    country: { key: 'country', sortable: false, defaultVisible: false },
    language: { key: 'language', sortable: false, defaultVisible: false },
    category: { key: 'category', sortable: false, defaultVisible: false },
    example: { key: 'example', sortable: false, defaultVisible: false },
    inserted_at: { key: 'inserted_at', sortable: true, defaultVisible: false },
    last_updated_at: { key: 'last_updated_at', sortable: true, defaultVisible: false },
};

// Get label for a column key
const getColumnLabel = (key) => {
    const labelMap = {
        sale: 'Price',
        url: 'Domain',
        da: 'DA',
        dr: 'DR',
        traffic: 'Traffic',
        link_type: 'Link Type',
        branded_content: 'Branded Content',
        types: 'Accepts niche',
        country: 'Country',
        language: 'Language',
        category: 'Category',
        example: 'Example URL',
        inserted_at: 'Upload Date',
        last_updated_at: 'Update Date',
    };
    return $t(labelMap[key] || key);
};

// Initialize columns from storage or defaults
const storedColumnsRaw = useStorage('sites.admin.index.columns', null);

// Build initial columns
const initialColumns = storedColumnsRaw.value && Array.isArray(storedColumnsRaw.value)
    ? storedColumnsRaw.value.filter(col => validColumnKeys.includes(col.key))
    : null;

// If no valid stored columns or invalid columns found, use defaults
if (!initialColumns || initialColumns.length === 0) {
    const defaultCols = validColumnKeys.map(key => ({
        key,
        visible: columnDefinitions[key].defaultVisible,
        sortable: columnDefinitions[key].sortable,
    }));
    storedColumnsRaw.value = defaultCols;
}

// Internal ref for column order and visibility (without labels)
const columnsData = ref(storedColumnsRaw.value || []);

// Watch for changes to columnsData and save to storage
watch(columnsData, (newColumns) => {
    storedColumnsRaw.value = newColumns.map(col => ({
        key: col.key,
        visible: col.visible,
        sortable: col.sortable
    }));
}, { deep: true });

// Computed columns with labels always applied
const columns = computed({
    get() {
        return columnsData.value.map(col => ({
            ...col,
            label: getColumnLabel(col.key)
        }));
    },
    set(newValue) {
        // When draggable changes order, update columnsData (labels will be stripped)
        columnsData.value = newValue.map(col => ({
            key: col.key,
            visible: col.visible,
            sortable: col.sortable
        }));
    }
});

const visibleColumns = computed(() => columns.value.filter((column) => column.visible))

// Column visibility menu
const columnMenuRef = ref();
const showColumnMenu = ref(false);

const toggleColumnMenu = (event) => {
    showColumnMenu.value = !showColumnMenu.value;
    event.stopPropagation();
};

const sort = ref(props.filters.sort)

const perPage = ref(props.filters.per_page || 10) 

const perPageOptions = computed(() => [
    { label: $t('5 Results'), value: 5 },
    { label: $t('10 Results'), value: 10 },
    { label: $t('15 Results'), value: 15 },
    { label: $t('20 Results'), value: 20 },
    { label: $t('50 Results'), value: 50 },
    { label: $t('100 Results'), value: 100 },
])

// Parse sort for DataTable
const parsedSort = computed(() => {
    const sortValue = sort.value || 'url';
    const isDesc = sortValue.startsWith('-');
    return {
        field: isDesc ? sortValue.substring(1) : sortValue,
        order: isDesc ? -1 : 1
    };
})

// Admin-specific filters
const filters = reactive({
    url: props.filters.filter.url,
    sale: { from: props.filters.filter.sale.from, to: props.filters.filter.sale.to },
    da: { from: props.filters.filter.da.from, to: props.filters.filter.da.to },
    dr: { from: props.filters.filter.dr.from, to: props.filters.filter.dr.to },
    traffic: { from: props.filters.filter.traffic.from, to: props.filters.filter.traffic.to },
    sponsor: props.filters.filter.sponsor,
    follow: props.filters.filter.follow,
    new: props.filters.filter.new || false,
    recommended: props.filters.filter.recommended || false,
    gambling: props.filters.filter.gambling || false,
    link_type: props.filters.filter.link_type
        ? (Array.isArray(props.filters.filter.link_type)
            ? props.filters.filter.link_type
            : [props.filters.filter.link_type])
        : [],
    language_id: props.filters.filter.language_id
        ? (Array.isArray(props.filters.filter.language_id)
            ? props.filters.filter.language_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.language_id)])
        : [],
    country_id: props.filters.filter.country_id
        ? (Array.isArray(props.filters.filter.country_id)
            ? props.filters.filter.country_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.country_id)])
        : [],
    category_id: props.filters.filter.category_id
        ? (Array.isArray(props.filters.filter.category_id)
            ? props.filters.filter.category_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.category_id)])
        : [],
    seller_id: props.filters.filter.seller_id
        ? (Array.isArray(props.filters.filter.seller_id)
            ? props.filters.filter.seller_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.seller_id)])
        : [],
    types: props.filters.filter.types
        ? (Array.isArray(props.filters.filter.types)
            ? props.filters.filter.types.map(id => parseInt(id))
            : [parseInt(props.filters.filter.types)])
        : [],
})

// No need for prop watcher since we're using preserveState
// The filters will maintain their state across requests

const debouncedGet = (() => {
    let timeoutId = null;
    return (delay = 300) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            get();
            timeoutId = null;
        }, delay);
    };
})();

watch(sort, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        debouncedGet(200);
    }
});

watch(perPage, (newValue, oldValue) => {
    if (newValue !== oldValue && newValue && newValue > 0) {
        debouncedGet(300);
    }
});

// List loading state
const isListLoading = ref(false);

// AbortController for request cancellation
const currentRequestController = ref(null);

// Handler for filter updates from SitesFilters component
const handleFiltersUpdate = (newFilters) => {
    // Mutate the reactive object directly instead of replacing it
    Object.keys(newFilters).forEach(key => {
        if (typeof newFilters[key] === 'object' && newFilters[key] !== null && !Array.isArray(newFilters[key])) {
            // For nested objects like sale, da, dr, traffic
            Object.keys(newFilters[key]).forEach(nestedKey => {
                filters[key][nestedKey] = newFilters[key][nestedKey];
            });
        } else {
            // For primitive values and arrays
            filters[key] = newFilters[key];
        }
    });
};

let stopAutoWatcher = null;

const stopWatcher = () => {
    if (stopAutoWatcher) {
        stopAutoWatcher();
        stopAutoWatcher = null;
    }
};

const handleResetFilters = async () => {
    stopWatcher();

    filters.url = '';
    filters.sale.from = '';
    filters.sale.to = '';
    filters.da.from = '';
    filters.da.to = '';
    filters.dr.from = '';
    filters.dr.to = '';
    filters.traffic.from = '';
    filters.traffic.to = '';
    filters.language_id = [];
    filters.country_id = [];
    filters.category_id = [];
    filters.seller_id = [];
    filters.types = [];
    filters.sponsor = false;
    filters.follow = false;
    filters.recommended = false;
    filters.new = false;
    filters.gambling = false;
    filters.link_type = [];

    await nextTick();

    // Immediately trigger search after reset
    get();

    startAutoWatcher();
}

const startAutoWatcher = () => {
    
    stopWatcher();

    stopAutoWatcher = watchDebounced(() => ({ ...filters }), (n, o) => {
        get();
    }, {
        debounce: 1000,
        deep: true,
    });
};

startAutoWatcher();

// Filters modal
const openFiltersModal = ref(false)

watch(openFiltersModal, async (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
        const activeElement = document.activeElement;
        if (activeElement && activeElement.tagName === 'INPUT') {
            activeElement.blur();
        }

        await nextTick();

        setTimeout(() => {
            startAutoWatcher();
        }, 100);
    }
});

// Helper function to remove empty/null/undefined values from filter object
const cleanFilters = (filters) => {
    const cleaned = {};

    for (const [key, value] of Object.entries(filters)) {
        // Skip if value is null, undefined, empty string, or empty array
        if (value === null || value === undefined || value === '') {
            continue;
        }

        // Handle arrays
        if (Array.isArray(value)) {
            if (value.length > 0) {
                cleaned[key] = value;
            }
            continue;
        }

        // Handle objects (like sale, da, dr, traffic)
        if (typeof value === 'object') {
            const cleanedObject = {};
            let hasValues = false;

            for (const [subKey, subValue] of Object.entries(value)) {
                if (subValue !== null && subValue !== undefined && subValue !== '') {
                    cleanedObject[subKey] = subValue;
                    hasValues = true;
                }
            }

            if (hasValues) {
                cleaned[key] = cleanedObject;
            }
            continue;
        }

        // Handle booleans - only include if true
        if (typeof value === 'boolean') {
            if (value === true) {
                cleaned[key] = value;
            }
            continue;
        }

        // Include all other values
        cleaned[key] = value;
    }

    return cleaned;
};

const get = async () => {
    try {
        // Cancel previous request if exists
        if (currentRequestController.value) {
            currentRequestController.value.abort();
        }

        // Create new abort controller
        currentRequestController.value = new AbortController();

        router.get(proxy.route ? proxy.route('sites.index') : '/admin/sites', {
            sort: sort.value,
            ratios: coinStore.ratios,
            filter: cleanFilters({
                url: filters.url,
                sale: filters.sale,
                da: filters.da,
                dr: filters.dr,
                traffic: filters.traffic,
                sponsor: filters.sponsor,
                // follow: filters.follow, // Disabled due to backend SQL error: Column 'offer_type.follow' not found
                new: filters.new,
                recommended: filters.recommended,
                gambling: filters.gambling,
                link_type: filters.link_type,
                language_id: Array.isArray(filters.language_id) && filters.language_id.length > 0 ? filters.language_id : [],
                country_id: Array.isArray(filters.country_id) && filters.country_id.length > 0 ? filters.country_id : [],
                category_id: Array.isArray(filters.category_id) && filters.category_id.length > 0 ? filters.category_id : [],
                seller_id: Array.isArray(filters.seller_id) && filters.seller_id.length > 0 ? filters.seller_id : [],
                types: Array.isArray(filters.types) && filters.types.length > 0 ? filters.types.join(',') : null,
            }),
            page: 1, // Reset to page 1 when filters change
            per_page: perPage.value,
        }, {
            preserveState: true,
            preserveScroll: true,
            only: ['sites', 'filters'],
            onStart: () => {
                isListLoading.value = true;
            },
            onFinish: () => {
                isListLoading.value = false;
                currentRequestController.value = null;
            },
            onError: () => {
                isListLoading.value = false;
                currentRequestController.value = null;
            },
            signal: currentRequestController.value?.signal
        })
    } catch (error) {
        console.error('Error in get function:', error);
        isListLoading.value = false;
        currentRequestController.value = null;
    }
}

// AI Search functionality
const openAiSearch = ref(false)
const aiSearchLoading = ref(false)
const aiSearchQuery = ref('')


// Active filters count
const activeFiltersCount = computed(() => {
    let count = 0

    // Check URL filter
    if (filters.url) count++

    // Check range filters (only if different from defaults)
    if (filters.sale.from || filters.sale.to) count++
    if (filters.da.from || filters.da.to) count++
    if (filters.dr.from || filters.dr.to) count++
    if (filters.traffic.from || filters.traffic.to) count++

    // Check boolean filters
    if (filters.sponsor) count++
    if (filters.follow) count++
    if (filters.new) count++
    if (filters.recommended) count++
    if (filters.gambling) count++
    if (filters.link_type && filters.link_type.length > 0) count++

    // Check array filters
    if (filters.language_id && filters.language_id.length > 0) count++
    if (filters.country_id && filters.country_id.length > 0) count++
    if (filters.category_id && filters.category_id.length > 0) count++
    if (filters.seller_id && filters.seller_id.length > 0) count++
    if (filters.types && filters.types.length > 0) count++

    return count
})

const handleAiSearch = () => {
    aiSearchLoading.value = true

    try {
        // Full-page loading is handled automatically by app.js for page navigations
        router.get(proxy.route ? proxy.route('sites.aiSearch') : '/admin/sites/ai-search', {
            text: aiSearchQuery.value,
        }, {
            preserveScroll: true,
            onFinish() {
                aiSearchLoading.value = false
                openAiSearch.value = false
            },
            onError: () => {
                aiSearchLoading.value = false
            }
        })
    } catch (error) {
        console.error('Error in AI search:', error);
        aiSearchLoading.value = false
        openAiSearch.value = false
    }
}

// Export functionality
const openExportDialog = ref(false)
const isExporting = ref(false)

const EXPORT_COLUMN_GROUPS = [
    {
        label: 'Basic',
        columns: [
            { key: 'id', label: 'ID' },
            { key: 'url', label: 'URL' },
            { key: 'da', label: 'DA' },
            { key: 'dr', label: 'DR' },
            { key: 'traffic', label: 'Traffic' },
            { key: 'status', label: 'Status' },
        ],
    },
    {
        label: 'Price',
        columns: [
            { key: 'display_cost', label: 'Display Cost' },
            { key: 'display_cost_coin', label: 'Display Cost Unit' },
            { key: 'display_sale', label: 'Display Sell' },
            { key: 'display_sale_coin', label: 'Display Sell Unit' },
        ],
    },
    {
        label: 'Relations',
        columns: [
            { key: 'seller', label: 'Seller' },
            { key: 'country', label: 'Country' },
            { key: 'language', label: 'Language' },
            { key: 'category', label: 'Category' },
            { key: 'types', label: 'Types' },
        ],
    },
    {
        label: 'Attributes',
        columns: [
            { key: 'casino', label: 'Casino' },
            { key: 'cbd', label: 'CBD' },
            { key: 'crypto', label: 'Crypto' },
            { key: 'sponsor', label: 'Sponsor' },
            { key: 'follow', label: 'Follow' },
            { key: 'recommended', label: 'Recommended' },
        ],
    },
    {
        label: 'Dates',
        columns: [
            { key: 'created_at', label: 'Created At' },
            { key: 'updated_at', label: 'Updated At' },
        ],
    },
]

const ALL_EXPORT_COLUMNS = EXPORT_COLUMN_GROUPS.flatMap(g => g.columns.map(c => c.key))

// Output options (format, columns, sort, limit) — filters live in customFilters / page filters
const exportOptions = reactive({
    format: 'csv',
    columns: [...ALL_EXPORT_COLUMNS],
    limit: 5000,
    sort: 'url',
    includeNiches: true,
    onlySelectedSellers: false,
})

// Mode: 'current' inherits page filters, 'custom' uses customFilters
const exportMode = ref('current')

// Custom search filters — mirror the page filters shape
const blankCustomFilters = () => ({
    url: '',
    sale: { from: null, to: null },
    da: { from: null, to: null },
    dr: { from: null, to: null },
    traffic: { from: null, to: null },
    sponsor: null,       // tri-state: true / false / null
    follow: null,
    new: null,
    recommended: null,
    gambling: null,
    link_type: [],
    language_id: [],
    country_id: [],
    category_id: [],
    seller_id: [],
    types: [],
})

const customFilters = reactive(blankCustomFilters())

const exportCount = ref(null) // { count, capped, cap, will_export }
const isCountingExport = ref(false)

const exportSortOptions = [
    { label: 'URL', value: 'url' },
    { label: 'Sale Price', value: 'sale' },
    { label: 'DA', value: 'da' },
    { label: 'DR', value: 'dr' },
    { label: 'Traffic', value: 'traffic' },
    { label: 'Date Added', value: 'inserted_at' },
    { label: 'Last Updated', value: 'last_updated_at' },
    { label: 'Orders Count', value: 'orders_count' },
]

const linkTypeOptions = [
    { label: 'Permanent', value: 'permanent' },
    { label: 'Temporary', value: 'temporary' },
]

const triStateOptions = [
    { label: 'Any', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false },
]

const isColumnSelected = (key) => exportOptions.columns.includes(key)

const toggleColumn = (key) => {
    const idx = exportOptions.columns.indexOf(key)
    if (idx === -1) {
        exportOptions.columns.push(key)
    } else {
        exportOptions.columns.splice(idx, 1)
    }
}

const selectAllColumns = () => { exportOptions.columns = [...ALL_EXPORT_COLUMNS] }
const deselectAllColumns = () => { exportOptions.columns = [] }

// ---------- Active page filter chips ----------
const findLabel = (collection, id, key = 'name') => {
    const item = (collection || []).find(c => Number(c.id) === Number(id))
    return item ? item[key] : `#${id}`
}

const formatPriceCents = (cents) => {
    const amount = (Number(cents) || 0) / 100
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const activePageFilters = computed(() => {
    const f = filters
    const chips = []

    if (f.url) chips.push(`${$t('Domain')}: ${f.url}`)
    if (f.sale.from || f.sale.to) {
        chips.push(`${$t('Price')}: ${f.sale.from ? formatPriceCents(f.sale.from) : '0'} – ${f.sale.to ? formatPriceCents(f.sale.to) : '∞'}`)
    }
    if (f.da.from || f.da.to) chips.push(`DA: ${f.da.from || 0} – ${f.da.to || '∞'}`)
    if (f.dr.from || f.dr.to) chips.push(`DR: ${f.dr.from || 0} – ${f.dr.to || '∞'}`)
    if (f.traffic.from || f.traffic.to) chips.push(`${$t('Traffic')}: ${f.traffic.from || 0} – ${f.traffic.to || '∞'}`)

    if (f.sponsor) chips.push($t('Sponsor'))
    if (f.follow) chips.push($t('Follow'))
    if (f.new) chips.push($t('New'))
    if (f.recommended) chips.push($t('Recommended'))
    if (f.gambling) chips.push($t('Gambling'))

    if (Array.isArray(f.link_type) && f.link_type.length) {
        chips.push(`${$t('Link Type')}: ${f.link_type.join(', ')}`)
    }
    if (Array.isArray(f.seller_id) && f.seller_id.length) {
        chips.push(`${$t('Sellers')}: ${f.seller_id.map(id => findLabel(props.sellers, id)).join(', ')}`)
    }
    if (Array.isArray(f.category_id) && f.category_id.length) {
        chips.push(`${$t('Categories')}: ${f.category_id.map(id => findLabel(props.categories, id, 'title')).join(', ')}`)
    }
    if (Array.isArray(f.country_id) && f.country_id.length) {
        chips.push(`${$t('Countries')}: ${f.country_id.map(id => findLabel(props.countries, id)).join(', ')}`)
    }
    if (Array.isArray(f.language_id) && f.language_id.length) {
        chips.push(`${$t('Languages')}: ${f.language_id.map(id => findLabel(props.languages, id)).join(', ')}`)
    }
    if (Array.isArray(f.types) && f.types.length) {
        chips.push(`${$t('Types')}: ${f.types.map(id => findLabel(props.types, id)).join(', ')}`)
    }

    return chips
})

// ---------- Build export params (shared by count + export) ----------
const buildExportParams = () => {
    const params = new URLSearchParams()

    params.set('format', exportOptions.format)
    params.set('limit', String(exportOptions.limit || 5000))
    if (exportOptions.sort) params.set('sort', exportOptions.sort)
    params.set('include_niches', exportOptions.includeNiches ? '1' : '0')
    if (exportOptions.onlySelectedSellers) params.set('only_selected_sellers', '1')

    exportOptions.columns.forEach(col => params.append('columns[]', col))

    if (exportMode.value === 'current') {
        // Inherit only filter[*] params from the current page URL
        const currentUrl = new URL(window.location.href)
        const urlParams = new URLSearchParams(currentUrl.search)
        for (const [key, value] of urlParams.entries()) {
            if (key.startsWith('filter[')) params.append(key, value)
        }
    } else {
        const f = customFilters
        if (f.url) params.set('filter[url]', f.url)
        if (f.sale.from) params.set('filter[sale][from]', String(Number(f.sale.from) * 100))
        if (f.sale.to)   params.set('filter[sale][to]',   String(Number(f.sale.to)   * 100))
        if (f.da.from)   params.set('filter[da][from]',   String(f.da.from))
        if (f.da.to)     params.set('filter[da][to]',     String(f.da.to))
        if (f.dr.from)   params.set('filter[dr][from]',   String(f.dr.from))
        if (f.dr.to)     params.set('filter[dr][to]',     String(f.dr.to))
        if (f.traffic.from) params.set('filter[traffic][from]', String(f.traffic.from))
        if (f.traffic.to)   params.set('filter[traffic][to]',   String(f.traffic.to))

        if (f.sponsor !== null && f.sponsor !== undefined)         params.set('filter[sponsor]',     f.sponsor ? '1' : '0')
        if (f.follow !== null && f.follow !== undefined)           params.set('filter[follow]',      f.follow ? '1' : '0')
        if (f.new !== null && f.new !== undefined)                 params.set('filter[new]',         f.new ? '1' : '0')
        if (f.recommended !== null && f.recommended !== undefined) params.set('filter[recommended]', f.recommended ? '1' : '0')
        if (f.gambling !== null && f.gambling !== undefined)       params.set('filter[gambling]',    f.gambling ? '1' : '0')

        if (Array.isArray(f.link_type) && f.link_type.length) {
            f.link_type.forEach(v => params.append('filter[link_type][]', v))
        }
        if (Array.isArray(f.seller_id) && f.seller_id.length) {
            f.seller_id.forEach(v => params.append('filter[seller_id][]', String(v)))
        }
        if (Array.isArray(f.category_id) && f.category_id.length) {
            f.category_id.forEach(v => params.append('filter[category_id][]', String(v)))
        }
        if (Array.isArray(f.country_id) && f.country_id.length) {
            f.country_id.forEach(v => params.append('filter[country_id][]', String(v)))
        }
        if (Array.isArray(f.language_id) && f.language_id.length) {
            f.language_id.forEach(v => params.append('filter[language_id][]', String(v)))
        }
        if (Array.isArray(f.types) && f.types.length) {
            params.set('filter[types]', f.types.join(','))
        }
    }

    return params
}

// ---------- Count preview (debounced) ----------
let countAbortController = null
const fetchExportCount = useDebounceFn(async () => {
    try {
        if (countAbortController) countAbortController.abort()
        countAbortController = new AbortController()
        isCountingExport.value = true

        const params = buildExportParams()
        params.set('cap', String(exportOptions.limit || 5000))
        const base = proxy.route ? proxy.route('sites.export-count') : '/admin/sites/export-count'
        const res = await fetch(base + '?' + params.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: { 'Accept': 'application/json' },
            signal: countAbortController.signal,
        })
        if (!res.ok) throw new Error('Count failed')
        exportCount.value = await res.json()
    } catch (e) {
        if (e.name !== 'AbortError') {
            console.error('Error fetching export count:', e)
            exportCount.value = null
        }
    } finally {
        isCountingExport.value = false
        countAbortController = null
    }
}, 400)

// Pre-fill custom filters from current page filters when dialog opens
watch(openExportDialog, (open) => {
    if (!open) return
    const fp = filters
    Object.assign(customFilters, {
        url: fp.url || '',
        sale: { from: fp.sale.from || null, to: fp.sale.to || null },
        da: { from: fp.da.from || null, to: fp.da.to || null },
        dr: { from: fp.dr.from || null, to: fp.dr.to || null },
        traffic: { from: fp.traffic.from || null, to: fp.traffic.to || null },
        sponsor: fp.sponsor ? true : null,
        follow: fp.follow ? true : null,
        new: fp.new ? true : null,
        recommended: fp.recommended ? true : null,
        gambling: fp.gambling ? true : null,
        link_type: [...(fp.link_type || [])],
        language_id: [...(fp.language_id || [])],
        country_id: [...(fp.country_id || [])],
        category_id: [...(fp.category_id || [])],
        seller_id: [...(fp.seller_id || [])],
        types: [...(fp.types || [])],
    })
    exportMode.value = 'current'
    exportCount.value = null
    fetchExportCount()
})

// Re-fetch count whenever mode/customFilters/limit changes while dialog open
watchDebounced(
    [() => exportMode.value, customFilters, () => exportOptions.limit],
    () => {
        if (openExportDialog.value) fetchExportCount()
    },
    { debounce: 400, deep: true }
)

const handleExport = async () => {
    if (exportOptions.columns.length === 0) {
        proxy.$toast.add({
            severity: 'warn',
            summary: $t('No columns selected'),
            detail: $t('Please select at least one column to export.'),
            life: 4000,
        })
        return
    }

    try {
        openExportDialog.value = false

        setTimeout(() => { isExporting.value = true }, 100)

        const routeName = proxy.route ? proxy.route('sites.export') : '/admin/sites/export'
        const params = buildExportParams()
        const exportUrl = routeName + '?' + params.toString()
        const format = exportOptions.format

        const response = await fetch(exportUrl, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Accept': format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
        })

        if (!response.ok) throw new Error('Export failed')

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url

        const contentDisposition = response.headers.get('Content-Disposition')
        let filename = `sites_export.${format === 'csv' ? 'csv' : 'xlsx'}`
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '')
            }
        }
        a.download = filename
        document.body.appendChild(a)
        a.click()

        setTimeout(() => {
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
            isExporting.value = false
        }, 100)

    } catch (error) {
        console.error('Error exporting sites:', error)
        isExporting.value = false
        openExportDialog.value = false
        proxy.$toast.add({
            severity: 'error',
            summary: $t('Export failed'),
            detail: $t('An error occurred while exporting. Please try again.'),
            life: 5000,
        })
    }
}

// Import functionality
const importFilepond = ref(null)
const openImportDialog = ref(false)
const importFinished = ref(false)
const seller = ref(null)
const notifyAdded = ref(false)
const notifyUpdated = ref(false)

const uploadSites = (fieldName, file, metadata, load, error, progress, abort) => {
    try {
        router.post(proxy.route ? proxy.route('sites.import') : '/admin/sites/import', {
            file: file,
            seller_id: seller.value,
            notify_added: notifyAdded.value,
            notify_updated: notifyUpdated.value,
        }, {
            forceFormData: true,
            preserveScroll: true,
            preserveState: true,
            onStart: () => {
                showPageLoading('Importing sites...');
            },
            onError(err) {
                abort()
                hidePageLoading();
            },
            onSuccess(res) {
                load()
            },
            onProgress(p) {
                progress(true, p.loaded, p.total);
            },
            onFinish() {
                importFinished.value = true
                hidePageLoading();
            }
        })
    } catch (err) {
        console.error('Error uploading sites:', err);
        abort();
        hidePageLoading();
    }
}

watch(openImportDialog, (n, o) => {
    if (!n) {
        seller.value = null
        notifyAdded.value = false
        notifyUpdated.value = false

        setTimeout(() => {
            importFinished.value = false
            router.reload({
                only: ['importFailures', 'importErrors', 'importAdded', 'importUpdated'],
            })
        }, 300)
    }
})

const coinFormat = computed(() => props.coins[coinStore.coin])
const expanded = ref([])
const { showPageLoading, hidePageLoading, showLocalLoading, hideLocalLoading } = useEnhancedLoading('admin-sites')

// currentRequestController is already declared above with isListLoading on line 187

// Column menu
const columnMenuVisible = ref(false)

// Helper function to open external site safely
const toggleRecommend = (site) => {
    confirm.require({
        message: $t('Are you sure you want to toggle recommendation for this site?'),
        header: $t('Confirm Action'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        acceptLabel: $t('Yes'),
        rejectLabel: $t('Cancel'),
        accept: () => {
            router.patch(proxy.route('sites.toggleRecommend', site.id), {}, {
                preserveState: true,
                preserveScroll: true,
                only: ['sites'],
                onSuccess: () => {
                    site.is_recommended = !site.is_recommended;
                },
                onError: (errors) => {
                    console.error('Error toggling recommendation:', errors);
                }
            });
        }
    });
}

// Blacklist site
const blacklistDialog = ref(false);
const blacklistReason = ref('');
const blacklistSite = ref(null);
const blacklistProcessing = ref(false);

const onBlacklistSite = (site) => {
    blacklistSite.value = site;
    blacklistReason.value = '';
    blacklistDialog.value = true;
};

const confirmBlacklistSite = () => {
    if (!blacklistSite.value) return;
    blacklistProcessing.value = true;
    router.post(proxy.route('sites.blacklist', blacklistSite.value.id), { reason: blacklistReason.value }, {
        preserveScroll: true,
        onSuccess: () => {
            blacklistDialog.value = false;
            blacklistSite.value = null;
            blacklistReason.value = '';
            proxy.$toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Site blacklisted successfully'),
                life: 3000,
            });
        },
        onError: () => {
            proxy.$toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to blacklist site'),
                life: 3000,
            });
        },
        onFinish: () => {
            blacklistProcessing.value = false;
        }
    });
};

const openExternalSite = (url) => {
    try {
        if (!url) {
            console.error('No URL provided');
            return;
        }

        // Remove any protocols and clean the URL
        const cleanUrl = url.replace(/^https?:\/\//, '').trim();

        if (!cleanUrl) {
            console.error('Invalid URL after cleaning:', url);
            return;
        }

        // Construct full URL with https
        const fullUrl = `https://${cleanUrl}`;
        console.log('Opening external site:', fullUrl);

        window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
        console.error('Error opening external site:', e, 'URL:', url);
    }
}

// Sort handler
const onSort = (event) => {
    // Spatie query builder uses minus prefix for descending sort
    sort.value = (event.sortOrder === 1 ? '' : '-') + event.sortField
}

// Admin-specific handlers for SitesTable events
const handleAdminPageChange = ({ event, page }) => {
    try {
        if (event) {
            // Desktop pagination - event.page is 0-indexed, add 1 for Laravel
            const targetPage = event.page + 1;

            isListLoading.value = true;

            router.get(proxy.route ? proxy.route('sites.index') : '/admin/sites', {
                sort: sort.value,
                ratios: coinStore.ratios,
                filter: cleanFilters({
                    url: filters.url,
                    sale: filters.sale,
                    da: filters.da,
                    dr: filters.dr,
                    traffic: filters.traffic,
                    sponsor: filters.sponsor,
                    new: filters.new,
                    recommended: filters.recommended,
                    gambling: filters.gambling,
                    language_id: Array.isArray(filters.language_id) && filters.language_id.length > 0 ? filters.language_id : [],
                    country_id: Array.isArray(filters.country_id) && filters.country_id.length > 0 ? filters.country_id : [],
                    category_id: Array.isArray(filters.category_id) && filters.category_id.length > 0 ? filters.category_id : [],
                    seller_id: Array.isArray(filters.seller_id) && filters.seller_id.length > 0 ? filters.seller_id : [],
                    types: Array.isArray(filters.types) && filters.types.length > 0 ? filters.types.join(',') : null,
                }),
                page: targetPage,
                per_page: perPage.value,
            }, {
                only: ['sites', 'filters'],
                preserveScroll: true,
                preserveState: true,
                onFinish: () => {
                    isListLoading.value = false;
                },
                onError: () => {
                    isListLoading.value = false;
                }
            });
        } else {
            // Mobile pagination
            const targetPage = page;
            isListLoading.value = true;

            router.get(proxy.route ? proxy.route('sites.index') : '/admin/sites', {
                sort: sort.value,
                ratios: coinStore.ratios,
                filter: cleanFilters({
                    url: filters.url,
                    sale: filters.sale,
                    da: filters.da,
                    dr: filters.dr,
                    traffic: filters.traffic,
                    sponsor: filters.sponsor,
                    new: filters.new,
                    recommended: filters.recommended,
                    gambling: filters.gambling,
                    language_id: Array.isArray(filters.language_id) && filters.language_id.length > 0 ? filters.language_id : [],
                    country_id: Array.isArray(filters.country_id) && filters.country_id.length > 0 ? filters.country_id : [],
                    category_id: Array.isArray(filters.category_id) && filters.category_id.length > 0 ? filters.category_id : [],
                    seller_id: Array.isArray(filters.seller_id) && filters.seller_id.length > 0 ? filters.seller_id : [],
                    types: Array.isArray(filters.types) && filters.types.length > 0 ? filters.types.join(',') : null,
                }),
                page: targetPage,
                per_page: perPage.value,
            }, {
                only: ['sites', 'filters'],
                preserveScroll: true,
                preserveState: true,
                onFinish: () => {
                    isListLoading.value = false;
                },
                onError: () => {
                    isListLoading.value = false;
                }
            });
        }
    } catch (e) {
        console.error('Pagination error:', e);
        isListLoading.value = false;
    }
}

const handleEditSite = (site) => {
    try {
        const routeName = proxy.route ? proxy.route('sites.edit', site.id) : `/admin/sites/${site.id}/edit`;
        router.visit(routeName, {
            onStart: () => showPageLoading($t('Loading...')),
            onFinish: () => hidePageLoading(),
            onError: () => hidePageLoading()
        });
    } catch (e) {
        console.error(e);
        hidePageLoading();
    }
}

const defaultColumnsData = columnsData.value.map(col => ({ ...col }));

const resetToDefault = () => {
  columnsData.value = defaultColumnsData.map(col => ({ ...col }));
};

onMounted(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlPerPage = urlParams.get('per_page');
    
    if (perPage.value === 10 && urlPerPage && urlPerPage !== '10') {
        urlParams.delete('per_page');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
        
        setTimeout(() => {
            get();
        }, 100);
    }

    // Initialize tooltips
    setTimeout(() => {
        if (window.tippy) {
            window.tippy('[data-tippy-content]');
        }
    }, 500)

    // Close column menu when clicking outside
    document.addEventListener('click', (e) => {
        if (columnMenuRef.value && !columnMenuRef.value.contains(e.target) &&
            !e.target.closest('.column-menu-button')) {
            showColumnMenu.value = false;
        }
    });
})
</script>

<template>
    <!-- Confirmation Dialog -->
    <ConfirmDialog />

    <!-- AI Search Dialog -->
    <Dialog v-model:visible="openAiSearch" modal :header="$t('Search with AI')" :style="{ width: '95vw', maxWidth: '25rem' }" :breakpoints="{ '960px': '75vw', '641px': '95vw' }">
        <div class="flex flex-col gap-4">
            <Textarea
                v-model="aiSearchQuery"
                rows="4"
                class="w-full"
                :placeholder="$t('Describe what kind of websites you are looking for...')"
            />
        </div>
        <template #footer>
            <Button
                :label="$t('Cancel')"
                severity="secondary"
                @click="openAiSearch = false"
                autofocus
            />
            <Button
                :label="aiSearchLoading ? $t('Searching...') : $t('Search')"
                :disabled="aiSearchLoading"
                @click="handleAiSearch"
            />
        </template>
    </Dialog>

    <!-- Import Dialog -->
    <Dialog v-model:visible="openImportDialog" modal header="Import Sites" :style="{ width: '95vw', maxWidth: '40rem' }" :breakpoints="{ '960px': '75vw', '641px': '95vw' }">
        <div class="flex flex-col gap-4">
            <FileUpload
                ref="importFilepond"
                mode="basic"
                name="file"
                accept=".csv,.xlsx,.xls"
                :maxFileSize="5000000"
                :auto="true"
                choose-label="Choose CSV or Excel File"
                @upload="uploadSites"
            />

            <div v-if="!importFinished">
                <Select
                    v-model="seller"
                    :options="sellers"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select a seller"
                    class="w-full"
                />

                <div class="flex flex-col gap-2 mt-4">
                    <div class="flex items-center">
                        <Checkbox v-model="notifyAdded" inputId="notify-added" :binary="true" />
                        <label for="notify-added" class="ml-2">Notify added sites</label>
                    </div>
                    <div class="flex items-center">
                        <Checkbox v-model="notifyUpdated" inputId="notify-updated" :binary="true" />
                        <label for="notify-updated" class="ml-2">Notify updated sites</label>
                    </div>
                </div>
            </div>

            <div v-if="importFinished" class="text-center">
                <div v-if="importAdded">{{ importAdded.length }} sites added.</div>
                <div v-if="importUpdated">{{ importUpdated.length }} sites updated.</div>
                <div v-if="importFailures">{{ importFailures.length }} sites failed.</div>
                <div v-if="importErrors">{{ importErrors.length }} sites with errors.</div>
            </div>
        </div>
    </Dialog>

    <!-- Export Dialog -->
    <Dialog v-model:visible="openExportDialog" modal :header="$t('Export Sites')" :style="{ width: '95vw', maxWidth: '56rem' }" :breakpoints="{ '960px': '80vw', '641px': '95vw' }">
        <div class="flex flex-col gap-5">

            <!-- Mode selector: Current Results vs Custom Search (card style, matches Format selector) -->
            <div>
                <div class="text-xs font-semibold uppercase tracking-wider text-surface-500 mb-2">{{ $t('What to export') }}</div>
                <div class="flex gap-3">
                    <div
                        class="flex-1 p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-primary-500"
                        :class="exportMode === 'current' ? 'border-primary-500 bg-primary-50' : 'border-surface-200'"
                        @click="exportMode = 'current'"
                    >
                        <div class="flex items-center gap-2">
                            <i class="pi pi-list text-xl" :class="exportMode === 'current' ? 'text-primary-600' : 'text-surface-400'"></i>
                            <div class="flex-1">
                                <div class="font-semibold text-sm">{{ $t('Current Results') }}</div>
                                <div class="text-xs text-surface-500">{{ $t('Use active list filters') }}</div>
                            </div>
                            <i class="pi pi-check-circle" :class="exportMode === 'current' ? 'text-primary-600' : 'text-surface-300'"></i>
                        </div>
                    </div>
                    <div
                        class="flex-1 p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-primary-500"
                        :class="exportMode === 'custom' ? 'border-primary-500 bg-primary-50' : 'border-surface-200'"
                        @click="exportMode = 'custom'"
                    >
                        <div class="flex items-center gap-2">
                            <i class="pi pi-search text-xl" :class="exportMode === 'custom' ? 'text-primary-600' : 'text-surface-400'"></i>
                            <div class="flex-1">
                                <div class="font-semibold text-sm">{{ $t('Custom Search') }}</div>
                                <div class="text-xs text-surface-500">{{ $t('Build a separate search') }}</div>
                            </div>
                            <i class="pi pi-check-circle" :class="exportMode === 'custom' ? 'text-primary-600' : 'text-surface-300'"></i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Mode body: Current Results -->
            <div v-if="exportMode === 'current'" class="flex flex-col gap-3">
                <div v-if="activePageFilters.length > 0" class="flex flex-wrap gap-2">
                    <Tag
                        v-for="(chip, idx) in activePageFilters"
                        :key="idx"
                        :value="chip"
                        severity="info"
                        rounded
                    />
                </div>
                <div v-else class="text-sm italic text-surface-500">
                    {{ $t('No filters active — all sites will be exported.') }}
                </div>
            </div>

            <!-- Mode body: Custom Search -->
            <div v-else class="flex flex-col gap-4">
                <!-- Domain + Numeric ranges -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1 sm:col-span-2">
                        <label class="text-sm text-surface-600">{{ $t('Domain contains') }}</label>
                        <InputText v-model="customFilters.url" class="w-full" size="small" />
                    </div>

                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Sale Price (USD)') }}</label>
                        <div class="flex gap-2">
                            <InputNumber v-model="customFilters.sale.from" :placeholder="$t('Min')" :min="0" :minFractionDigits="0" :maxFractionDigits="2" class="w-full" size="small" />
                            <InputNumber v-model="customFilters.sale.to" :placeholder="$t('Max')" :min="0" :minFractionDigits="0" :maxFractionDigits="2" class="w-full" size="small" />
                        </div>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">DA</label>
                        <div class="flex gap-2">
                            <InputNumber v-model="customFilters.da.from" :placeholder="$t('Min')" :min="0" :max="100" class="w-full" size="small" />
                            <InputNumber v-model="customFilters.da.to" :placeholder="$t('Max')" :min="0" :max="100" class="w-full" size="small" />
                        </div>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">DR</label>
                        <div class="flex gap-2">
                            <InputNumber v-model="customFilters.dr.from" :placeholder="$t('Min')" :min="0" :max="100" class="w-full" size="small" />
                            <InputNumber v-model="customFilters.dr.to" :placeholder="$t('Max')" :min="0" :max="100" class="w-full" size="small" />
                        </div>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Traffic') }}</label>
                        <div class="flex gap-2">
                            <InputNumber v-model="customFilters.traffic.from" :placeholder="$t('Min')" :min="0" class="w-full" size="small" />
                            <InputNumber v-model="customFilters.traffic.to" :placeholder="$t('Max')" :min="0" class="w-full" size="small" />
                        </div>
                    </div>
                </div>

                <!-- Multi-select relations -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Sellers') }}</label>
                        <MultiSelect v-model="customFilters.seller_id" :options="sellers" optionLabel="name" optionValue="id" :placeholder="$t('Any')" filter class="w-full" size="small" />
                        <div class="flex items-center justify-between mt-1 px-1">
                            <span class="text-xs text-surface-500">{{ $t('Incluir apenas ofertas dos sellers selecionados') }}</span>
                            <ToggleSwitch v-model="exportOptions.onlySelectedSellers" />
                        </div>
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Categories') }}</label>
                        <MultiSelect v-model="customFilters.category_id" :options="categories" optionLabel="title" optionValue="id" :placeholder="$t('Any')" filter class="w-full" size="small" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Countries') }}</label>
                        <MultiSelect v-model="customFilters.country_id" :options="countries" optionLabel="name" optionValue="id" :placeholder="$t('Any')" filter class="w-full" size="small" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Languages') }}</label>
                        <MultiSelect v-model="customFilters.language_id" :options="languages" optionLabel="name" optionValue="id" :placeholder="$t('Any')" filter class="w-full" size="small" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Types') }}</label>
                        <MultiSelect v-model="customFilters.types" :options="types" optionLabel="name" optionValue="id" :placeholder="$t('Any')" filter class="w-full" size="small" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Link Type') }}</label>
                        <MultiSelect v-model="customFilters.link_type" :options="linkTypeOptions" optionLabel="label" optionValue="value" :placeholder="$t('Any')" class="w-full" size="small" />
                    </div>
                </div>

                <!-- Tri-state attribute toggles -->
                <div>
                    <div class="text-xs text-surface-500 mb-2">{{ $t('Attributes') }}</div>
                    <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div class="flex flex-col gap-1">
                            <label class="text-sm text-surface-600">{{ $t('Sponsor') }}</label>
                            <Select v-model="customFilters.sponsor" :options="triStateOptions" optionLabel="label" optionValue="value" class="w-full" size="small" />
                        </div>
                        <div class="flex flex-col gap-1">
                            <label class="text-sm text-surface-600">{{ $t('Follow') }}</label>
                            <Select v-model="customFilters.follow" :options="triStateOptions" optionLabel="label" optionValue="value" class="w-full" size="small" />
                        </div>
                        <div class="flex flex-col gap-1">
                            <label class="text-sm text-surface-600">{{ $t('New') }}</label>
                            <Select v-model="customFilters.new" :options="triStateOptions" optionLabel="label" optionValue="value" class="w-full" size="small" />
                        </div>
                        <div class="flex flex-col gap-1">
                            <label class="text-sm text-surface-600">{{ $t('Recommended') }}</label>
                            <Select v-model="customFilters.recommended" :options="triStateOptions" optionLabel="label" optionValue="value" class="w-full" size="small" />
                        </div>
                        <div class="flex flex-col gap-1">
                            <label class="text-sm text-surface-600">{{ $t('Gambling') }}</label>
                            <Select v-model="customFilters.gambling" :options="triStateOptions" optionLabel="label" optionValue="value" class="w-full" size="small" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Format -->
            <div>
                <div class="text-xs font-semibold uppercase tracking-wider text-surface-500 mb-2">{{ $t('Format') }}</div>
                <div class="flex gap-3">
                    <div
                        class="flex-1 p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-primary-500"
                        :class="exportOptions.format === 'csv' ? 'border-primary-500 bg-primary-50' : 'border-surface-200'"
                        @click="exportOptions.format = 'csv'"
                    >
                        <div class="flex items-center gap-2">
                            <i class="pi pi-file text-xl" :class="exportOptions.format === 'csv' ? 'text-primary-600' : 'text-surface-400'"></i>
                            <div class="flex-1">
                                <div class="font-semibold text-sm">CSV</div>
                                <div class="text-xs text-surface-500">Excel / Google Sheets</div>
                            </div>
                            <i class="pi pi-check-circle" :class="exportOptions.format === 'csv' ? 'text-primary-600' : 'text-surface-300'"></i>
                        </div>
                    </div>
                    <div
                        class="flex-1 p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-primary-500"
                        :class="exportOptions.format === 'excel' ? 'border-primary-500 bg-primary-50' : 'border-surface-200'"
                        @click="exportOptions.format = 'excel'"
                    >
                        <div class="flex items-center gap-2">
                            <i class="pi pi-file-excel text-xl" :class="exportOptions.format === 'excel' ? 'text-primary-600' : 'text-surface-400'"></i>
                            <div class="flex-1">
                                <div class="font-semibold text-sm">Excel (.xlsx)</div>
                                <div class="text-xs text-surface-500">Microsoft Excel</div>
                            </div>
                            <i class="pi pi-check-circle" :class="exportOptions.format === 'excel' ? 'text-primary-600' : 'text-surface-300'"></i>
                        </div>
                    </div>
                </div>
            </div>

            <Divider class="my-0" />

            <!-- Columns -->
            <div>
                <div class="flex items-center justify-between mb-2">
                    <div class="text-xs font-semibold uppercase tracking-wider text-surface-500">{{ $t('Columns') }} ({{ exportOptions.columns.length }})</div>
                    <div class="flex gap-2">
                        <Button :label="$t('All')" size="small" severity="secondary" text @click="selectAllColumns" />
                        <Button :label="$t('None')" size="small" severity="secondary" text @click="deselectAllColumns" />
                    </div>
                </div>
                <div class="flex flex-col gap-3">
                    <div v-for="group in EXPORT_COLUMN_GROUPS" :key="group.label">
                        <div class="text-xs text-surface-400 mb-1">{{ $t(group.label) }}</div>
                        <div class="flex flex-wrap gap-x-4 gap-y-1">
                            <div v-for="col in group.columns" :key="col.key" class="flex items-center gap-1 cursor-pointer">
                                <Checkbox :modelValue="isColumnSelected(col.key)" binary :inputId="`export-col-${col.key}`" @update:modelValue="toggleColumn(col.key)" />
                                <label :for="`export-col-${col.key}`" class="text-sm cursor-pointer select-none">{{ $t(col.label) }}</label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div class="text-xs text-surface-400 mb-1">{{ $t('Niche Offers') }}</div>
                        <div class="flex items-center gap-2">
                            <Checkbox v-model="exportOptions.includeNiches" binary inputId="export-include-niches" />
                            <label for="export-include-niches" class="text-sm cursor-pointer select-none">
                                {{ $t('Include per-niche sale & cost columns') }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <Divider class="my-0" />

            <!-- Output (Sort + Limit) -->
            <div>
                <div class="text-xs font-semibold uppercase tracking-wider text-surface-500 mb-3">{{ $t('Output') }}</div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Sort by') }}</label>
                        <Select
                            v-model="exportOptions.sort"
                            :options="exportSortOptions"
                            optionLabel="label"
                            optionValue="value"
                            class="w-full"
                            size="small"
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-sm text-surface-600">{{ $t('Max records') }} <span class="text-surface-400">(max 10,000)</span></label>
                        <InputNumber
                            v-model="exportOptions.limit"
                            :min="1"
                            :max="10000"
                            :minFractionDigits="0"
                            :maxFractionDigits="0"
                            class="w-full"
                            size="small"
                        />
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex items-center justify-between gap-3 w-full">
                <div class="text-sm">
                    <template v-if="isCountingExport">
                        <i class="pi pi-spin pi-spinner mr-1 text-surface-500"></i>
                        <span class="text-surface-500">{{ $t('Counting…') }}</span>
                    </template>
                    <template v-else-if="exportCount">
                        <span class="font-semibold text-surface-800">{{ exportCount.count.toLocaleString() }}</span>
                        <span>&nbsp;</span>
                        <span class="text-surface-600">{{ $t('matching sites') }}</span>
                    </template>
                </div>
                <div class="flex gap-2">
                    <Button
                        :label="$t('Cancel')"
                        severity="secondary"
                        @click="openExportDialog = false"
                        outlined
                    />
                    <Button
                        :label="$t('Export')"
                        icon="pi pi-download"
                        :disabled="exportOptions.columns.length === 0"
                        @click="handleExport"
                    />
                </div>
            </div>
        </template>
    </Dialog>

    <!-- Export Loading Dialog -->
    <Dialog v-model:visible="isExporting" modal :header="$t('Exporting Sites')" :closable="false" :style="{ width: '95vw', maxWidth: '25rem' }" :breakpoints="{ '960px': '75vw', '641px': '95vw' }">
        <div class="flex flex-col items-center gap-4 py-4">
            <i class="pi pi-spin pi-spinner text-5xl text-primary-500"></i>
            <div class="text-center">
                <div class="font-semibold text-lg mb-2">{{ $t('Preparing your export...') }}</div>
                <div class="text-sm text-surface-600">{{ $t('Please wait, this may take a few moments') }}</div>
            </div>
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2 w-full">
                <div class="flex gap-2">
                    <i class="pi pi-info-circle text-blue-600 mt-0.5"></i>
                    <div class="text-sm text-blue-900">
                        {{ $t('Your download will start automatically when ready') }}
                    </div>
                </div>
            </div>
        </div>
    </Dialog>

    <!-- Sync Banner -->
    <SyncBanner />

    <!-- Filters Component -->
    <div class="px-2 sm:px-4 lg:px-0 mb-4">
        <SitesFilters
            :modelValue="filters"
            @update:modelValue="handleFiltersUpdate"
            @reset-filters="handleResetFilters"
            v-model:filtersModalVisible="openFiltersModal"
            :countries="countries"
            :languages="languages"
            :categories="categories"
            :sellers="sellers"
            :types="types"
            :sitesByCountry="sitesByCountry"
            :coinStore="coinStore"
            :showProjectFilter="false"
            :showFavoritesFilter="false"
            :showInterestsFilter="false"
            :showSellerFilter="true"
        />
    </div>

    <!-- Main Content -->
    <div class="px-2 sm:px-4 lg:px-0 mb-4">
        <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 lg:gap-4">
            <div class="flex-1 flex items-center gap-3">
                <span class="ms-2 text-lg text-surface-800">
                    {{ sites.total.toLocaleString() }} {{ $t('results found') }}
                </span>
                <div v-if="pendingCount > 0">
                    <Button
                        :label="`${pendingCount} ${$t('sites waiting for approval.')}`"
                        link
                        class="p-0 text-sm text-primary-500"
                        @click="() => {
                            try {
                                const routeName = proxy.route ? proxy.route('sites.index', { 'filter[of_status]': 'PENDING' }) : '/admin/sites';
                                router.visit(routeName);
                            } catch (e) { console.error(e); }
                        }"
                    />
                </div>
            </div>

            <!-- Mobile: Stacked buttons -->
            <div class="flex flex-col sm:flex-row gap-2 lg:hidden">
                <Button
                    :label="$t('Export')"
                    icon="pi pi-download"
                    severity="secondary"
                    @click="openExportDialog = true"
                    class="w-full sm:w-auto text-sm"
                    size="small"
                />
                <Button
                    :label="$t('Add site')"
                    icon="pi pi-plus"
                    @click="() => {
                        try {
                            const routeName = proxy.route ? proxy.route('sites.create') : '/admin/sites/create';
                            router.visit(routeName, {
                                onStart: () => showPageLoading($t('Loading...')),
                                onFinish: () => hidePageLoading(),
                                onError: () => hidePageLoading()
                            });
                        } catch (e) { console.error(e); hidePageLoading(); }
                    }"
                    class="w-full sm:w-auto text-sm"
                    size="small"
                />
            </div>

            <!-- Desktop: Horizontal buttons -->
            <div class="hidden lg:flex gap-2 relative">
                <Select
                    v-model="perPage"
                    :options="perPageOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="h-[31px] custom-select-center"
                    style="width: 150px;"
                    :showClear="false"
                />
                <Button
                    :label="$t('Columns')"
                    icon="pi pi-cog"
                    severity="secondary"
                    outlined
                    size="small"
                    @click="toggleColumnMenu"
                    class="column-menu-button h-[31px]"
                />
                <Button
                    :label="$t('Add site')"
                    icon="pi pi-plus"
                    size="small"
                    @click="() => {
                        try {
                            const routeName = proxy.route ? proxy.route('sites.create') : '/admin/sites/create';
                            router.visit(routeName, {
                                onStart: () => showPageLoading($t('Loading...')),
                                onFinish: () => hidePageLoading(),
                                onError: () => hidePageLoading()
                            });
                        } catch (e) { console.error(e); hidePageLoading(); }
                    }"
                />
                <Button
                    :label="$t('Export')"
                    icon="pi pi-download"
                    severity="secondary"
                    size="small"
                    @click="openExportDialog = true"
                />
                <div ref="columnMenuRef" v-show="showColumnMenu"
                     class="absolute right-0 top-10 z-50 bg-white rounded-lg shadow-lg border border-surface-200 column-menu">
                    <div class="p-3 border-b border-surface-200">
                        <div class="flex items-center justify-between mb-0">
                            <div class="font-semibold text-surface-700">{{ $t('Manage Columns') }}</div>
                            <Button
                                icon="pi pi-replay"
                                size="small"
                                outlined
                                class="uncheck-all-button"
                                @click="resetToDefault"
                                :pt="{
                                    root: {
                                        style: 'border-color: #ced4da !important;'
                                    },
                                    icon: {
                                        style: 'color: #adb5bd !important;'
                                    }
                                }"
                            />
                        </div>
                        <div class="text-sm text-surface-500">{{ $t('Drag to reorder, check to show/hide') }}</div>
                    </div>
                    <div class="p-3 max-h-96 overflow-y-auto">
                        <draggable
                            v-model="columnsData"
                            tag="div"
                            ghost-class="opacity-50"
                            handle=".drag-handle"
                            :animation="200"
                        >
                            <template #item="{element}">
                                <div class="flex items-center gap-2 p-2 hover:bg-surface-100 rounded cursor-move">
                                    <i class="pi pi-bars drag-handle text-surface-400 cursor-move"></i>
                                    <Checkbox
                                        v-model="element.visible"
                                        :binary="true"
                                        :inputId="`col-${element.key}`"
                                    />
                                    <label :for="`col-${element.key}`" class="flex-1 cursor-pointer text-sm select-none">
                                        {{ getColumnLabel(element.key) }}
                                    </label>
                                </div>
                            </template>
                        </draggable>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Sites Table Component -->
    <div class="px-2 sm:px-4 lg:px-0">
        <SitesTable
            :sites="sites"
            :sitesWithHighestSale="sitesWithHighestSale"
            :visibleColumns="visibleColumns"
            :isListLoading="isListLoading"
            :parsedSort="parsedSort"
            :coinStore="coinStore"
            :allTypes="types"
            mode="admin"
            :rocketSvg="rocketSvg"
            :rocketFillSvg="rocketFillSvg"
            :showPageLoading="showPageLoading"
            :hidePageLoading="hidePageLoading"
            @sort="onSort"
            @page-change="handleAdminPageChange"
            @edit-site="handleEditSite"
            @toggle-recommend="toggleRecommend"
            @blacklist-site="onBlacklistSite"
        />
    </div>

    <!-- Blacklist Site Dialog -->
    <Dialog v-model:visible="blacklistDialog" modal :header="$t('Blacklist site')" :style="{ width: '95vw', maxWidth: '32rem' }" :breakpoints="{ '641px': '95vw' }">
        <div class="mb-2">
            <p class="mb-3 text-sm text-gray-600" v-if="blacklistSite">
                {{ $t('This will blacklist the site and remove it from the catalog.') }}
                <span class="font-semibold">{{ blacklistSite.url }}</span>
            </p>
            <label for="blacklist-reason" class="font-semibold block mb-2">{{ $t('Reason (optional)') }}</label>
            <Textarea id="blacklist-reason" v-model="blacklistReason" class="w-full" rows="3" :disabled="blacklistProcessing" />
        </div>
        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-secondary" @click="blacklistDialog = false" :disabled="blacklistProcessing" />
            <Button :label="$t('Blacklist site')" icon="pi pi-ban" severity="danger" @click="confirmBlacklistSite" :loading="blacklistProcessing" :disabled="blacklistProcessing" />
        </template>
    </Dialog>
</template>

<style scoped>

.custom-select-center :deep(.p-select-label),
.custom-select-center :deep(.p-select-option),
.custom-select-center :deep(.p-select-dropdown .p-select-option) {
    white-space: nowrap !important;
    color: #64748b !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    font: 14px Archivo, Helvetica, Arial, sans-serif !important;
    padding: 6px 10px 6px 8px !important;
}

.custom-select-center :deep(.p-select-label) {
    display: flex !important;
    align-items: center !important;
    max-width: 120px !important;        
}

.column-menu :deep(.p-menu-list) {
    padding: 0;
}

.column-menu {
    min-width: 300px;
    max-width: 400px;
}

.column-menu :deep(.p-menu-item) {
    padding: 0;
}
.column-menu-button {
    border: 1px solid #d1d5db !important;
    border-radius: 6px !important;
}

.column-menu-button:hover {
    border-color: #9ca3af !important;
}
</style>
